import axios from "axios";
import { serverUrl } from "../nestserver";

/**
 * Group Console API — read-only, cross-branch views for a school-group admin.
 * Every list response comes back as { data, errors }: `errors` names any
 * branch whose read failed, so one flaky branch degrades the console instead
 * of breaking it. Mirrors the backend contract in
 * `src/group-console/group-console.service.ts`.
 */

const base = (groupId) => `${serverUrl}/group-console/${groupId}`;

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
  };
}

async function get(path, params) {
  const res = await axios.get(path, { params, headers: authHeaders() });
  return res.data?.result ?? { data: [], errors: [] };
}

async function post(path, payload) {
  const res = await axios.post(path, payload, { headers: authHeaders() });
  return res.data?.result ?? { created: [], errors: [] };
}

export const fetchGroupSummary = (groupId) => get(`${base(groupId)}/summary`);

export const fetchGroupStudents = (groupId, params) =>
  get(`${base(groupId)}/students`, params);

export const fetchGroupInstructors = (groupId, params) =>
  get(`${base(groupId)}/instructors`, params);

export const fetchGroupParents = (groupId, params) =>
  get(`${base(groupId)}/parents`, params);

export const fetchGroupAdmins = (groupId, params) =>
  get(`${base(groupId)}/admins`, params);

export const fetchGroupAdmissions = (groupId, params) =>
  get(`${base(groupId)}/admissions`, params);

export const fetchGroupFeeStats = (groupId) => get(`${base(groupId)}/fee-stats`);

// The group's Is-Kool-360° — the same 7 school-analytics calls the branch
// admin's own dashboard fires, merged across every branch into one shape
// each of its 6 real sections already knows how to render.
export const fetchGroupDashboard360 = (groupId, year) =>
  get(`${base(groupId)}/dashboard360`, { year });

export const fetchGroupExpenses = (groupId, params) =>
  get(`${base(groupId)}/expenses`, params);

export const fetchGroupStock = (groupId, params) =>
  get(`${base(groupId)}/stock`, params);

export const fetchGroupWakeupAlarms = (groupId, params) =>
  get(`${base(groupId)}/wakeup-alarms`, params);

// Write side: create only, targeting one branch or every branch at once via
// `payload.targetSchools` (an array of branch ids — the caller resolves
// "All Branches" to every id itself before calling these).
export const createGroupExpense = (groupId, payload) =>
  post(`${base(groupId)}/expenses`, payload);

export const createGroupStock = (groupId, payload) =>
  post(`${base(groupId)}/stock`, payload);

export const createGroupWakeupAlarm = (groupId, payload) =>
  post(`${base(groupId)}/wakeup-alarms`, payload);

// Course board has no create endpoint of its own here — the Group Console
// picks a branch and switches into it (useGroupBranchSwitch.js), landing on
// that branch's real, unmodified admin Course Board.

// A stock item lives in exactly one branch, so these three take that
// branch's school id (every row from `fetchGroupStock` already carries it as
// `row.school`) rather than `targetSchools` — nothing to fan out to. Gives a
// group admin the same edit/adjust/delete capability the branch admin's own
// Stock screen has, on any branch's catalogue.
async function patch(path, payload) {
  const res = await axios.patch(path, payload, { headers: authHeaders() });
  return res.data?.result;
}

async function del(path) {
  const res = await axios.delete(path, { headers: authHeaders() });
  return res.data?.result;
}

export const updateGroupStock = (groupId, school, id, payload) =>
  patch(`${base(groupId)}/stock/${school}/${id}`, payload);

export const adjustGroupStock = (groupId, school, id, payload) =>
  patch(`${base(groupId)}/stock/${school}/${id}/adjust`, payload);

export const deleteGroupStock = (groupId, school, id) =>
  del(`${base(groupId)}/stock/${school}/${id}`);
